import React, { useState, useCallback, useEffect, useRef } from 'react';
import { guestAPI, GuestEvent, GuestListResponse, GuestListParams, FollowUpRecipient } from '../../services/guestAPI';
import { GuestActionBar } from './GuestActionBar';
import { GuestDisplay } from './GuestDisplay';
import { AddGuestFormEnhanced } from './AddGuestFormEnhanced';
import { GuestImportModal } from './GuestImportModal';
import { EditCompanionModal } from './EditCompanionModal';
import { ImageMessageModal } from './ImageMessageModal';
import SendByNameModal from './SendByNameModal';
import Toast from '../UI/Toast';
import ConfirmationModal from '../UI/ConfirmationModal';
import BulkInviteConfirmationModal from './BulkInviteConfirmationModal';
import FollowUpConfirmationModal from './FollowUpConfirmationModal';
import { useAppSelector } from '../../store/hooks';
import { ExcelExportUtils } from '../../utils/excelExport';
import { webSocketService } from '../../services/websocketService';
import apiClient from '../../services/api';
import { logError } from '../../utils/errorLogger';
import { eventAPI } from '../../services/eventAPI';
import { Event } from '../../types/event';
import { hasEventEnded, canEditEvent } from '../../utils/eventUtils';
import { useAlert } from '../../contexts';
import { AlertTriangle } from 'lucide-react';
import { BillingActionPreview, CostBreakdown } from '../Billing';
import { billingService, PricingConfiguration, HostBalance } from '../../services/billingService';
import { useDebounce } from '../../hooks/useDebounce';
import { formatGuestEventDisplayName } from '../../utils/guestNameUtils';

interface GuestManagementProps {
  eventId: string;
  onGuestStatsUpdate?: () => void;
}

export const GuestManagement: React.FC<GuestManagementProps> = ({ eventId, onGuestStatsUpdate }) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { showConfirm } = useAlert();
  
  // State for guests and UI
  const [guests, setGuests] = useState<GuestEvent[]>([]);
  const [summary, setSummary] = useState<GuestListResponse['summary'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditCompanions, setShowEditCompanions] = useState(false);
  const [showImageMessageModal, setShowImageMessageModal] = useState(false);
  const [editingGuest, setEditingGuest] = useState<GuestEvent | null>(null);
  
  // Confirmation modal states
  const [showWhatsAppConfirm, setShowWhatsAppConfirm] = useState(false);
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [showFollowUpConfirm, setShowFollowUpConfirm] = useState(false);
  // Follow-up confirmation table: the resolved recipients and the rows the host unticked
  const [followUpRecipients, setFollowUpRecipients] = useState<FollowUpRecipient[]>([]);
  const [followUpExcludedIds, setFollowUpExcludedIds] = useState<Set<string>>(new Set());
  const [loadingFollowUpRecipients, setLoadingFollowUpRecipients] = useState(false);
  const [followUpLoadError, setFollowUpLoadError] = useState<string | null>(null);
  const [showSaveTheDateConfirm, setShowSaveTheDateConfirm] = useState(false);
  const [showImageMessageConfirm, setShowImageMessageConfirm] = useState(false);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const [pendingImageFilePath, setPendingImageFilePath] = useState<string | null>(null);
  const [pendingImageMessageBody, setPendingImageMessageBody] = useState('');
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  
  // Send by name modal states
  const [showSendByNameModal, setShowSendByNameModal] = useState(false);
  const [, setSendByName] = useState<boolean>(true);
  const [pendingSendType, setPendingSendType] = useState<'whatsapp' | 'reminder' | 'followup' | 'save_the_date' | null>(null);
  const [pendingSendByNameValue, setPendingSendByNameValue] = useState<boolean>(true);
  
  // Filter and view states
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const fetchRequestIdRef = useRef(0);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [importFilter, setImportFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<string>('-id');
  
  // Selection state
  const [selectedGuests, setSelectedGuests] = useState<Set<string>>(new Set());
  const [selectingAll, setSelectingAll] = useState(false);
  const [resolvedSelectedGuests, setResolvedSelectedGuests] = useState<GuestEvent[]>([]);
  const [resolvingSelection, setResolvingSelection] = useState(false);
  const [hasWhatsAppSentInSelection, setHasWhatsAppSentInSelection] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(21);
  
  // Toast messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Event data for checking if event has ended and can be edited
  const [event, setEvent] = useState<Event | null>(null);
  const [eventHasEnded, setEventHasEnded] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  
  // Billing state
  const [pricing, setPricing] = useState<PricingConfiguration | null>(null);
  const [balance, setBalance] = useState<HostBalance | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);

  // Fetch guests with pagination
  const fetchGuests = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      
      const trimmedSearch = debouncedSearchTerm.trim();
      const params: GuestListParams = {
        page: currentPage,
        page_size: pageSize,
      };
      if (statusFilter.length > 0) params.status = statusFilter as any;
      if (trimmedSearch) params.search = trimmedSearch;
      if (importFilter === 'imported') params.imported_only = true;
      if (importFilter === 'manual') params.manual_only = true;
      if (sortBy) params.ordering = sortBy;
      
      const response = await guestAPI.getGuests(eventId, params);

      if (requestId !== fetchRequestIdRef.current) return;
      
      setGuests(response.results);
      setSummary(response.summary);
      setTotalCount(response.count);
      setTotalPages(Math.ceil(response.count / pageSize));
    } catch (err: any) {
      if (requestId !== fetchRequestIdRef.current) return;
      logError('Error fetching guests', err);
      setError(err.response?.data?.message || 'Failed to load guests');
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [eventId, statusFilter, debouncedSearchTerm, importFilter, sortBy, currentPage, pageSize]);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);
  
  // Reset to page 1 when filters change (searchTerm, not debounced — so page is 1 before fetch)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, importFilter, sortBy]);
  
  // Fetch event data to check if it has ended and can be edited
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventData = await eventAPI.getEvent(eventId);
        setEvent(eventData);
        setEventHasEnded(hasEventEnded(eventData));
        setCanEdit(canEditEvent(eventData));
      } catch (err) {
        logError('Error fetching event data', err);
      }
    };
    
    fetchEvent();
  }, [eventId]);

  // Auto-refresh disabled - using real-time WebSocket updates instead
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     fetchGuests();
  //   }, 10000); // Refresh every 10 seconds
  //   
  //   return () => clearInterval(interval);
  // }, [fetchGuests]);

  // Real-time WebSocket updates
  useEffect(() => {
    const handleGuestResponse = (data: any) => {
      // Update only the specific guest's status instead of refreshing the entire list
      setGuests(prevGuests => 
        prevGuests.map(guest => {
          if (guest.id.toString() === data.guest_event_id) {
            return {
              ...guest,
              status: data.status,
              response_at: data.response_at || new Date().toISOString(),
              whatsapp_message_sid: data.whatsapp_response_sid || guest.whatsapp_message_sid
            };
          }
          return guest;
        })
      );
      
      // Update summary statistics
      if (summary) {
        setSummary(prevSummary => {
          if (!prevSummary) return prevSummary;
          
          const newSummary = { ...prevSummary };
          const oldStatus = data.old_status as keyof typeof prevSummary.status_breakdown;
          const newStatus = data.status as keyof typeof prevSummary.status_breakdown;
          
          // Decrease old status count
          if (oldStatus && oldStatus in newSummary.status_breakdown && newSummary.status_breakdown[oldStatus] > 0) {
            newSummary.status_breakdown[oldStatus]--;
          }
          
          // Increase new status count
          if (newStatus && newStatus in newSummary.status_breakdown) {
            newSummary.status_breakdown[newStatus]++;
          }
          
          // Recalculate response rate
          const totalResponses = newSummary.status_breakdown.accepted + newSummary.status_breakdown.rejected + newSummary.status_breakdown.ignored;
          newSummary.response_rate = newSummary.total_invited > 0 ? (totalResponses / newSummary.total_invited) * 100 : 0;
          
          return newSummary;
        });
        
        // Notify parent component to update guest stats (for top card and dashboard)
        onGuestStatsUpdate?.();
      }
    };

    const handleStatisticsUpdate = (data: any) => {
      
      // Update summary statistics directly instead of refreshing entire list
      if (summary) {
        setSummary(prevSummary => {
          if (!prevSummary) return prevSummary;
          
          const newSummary = {
            ...prevSummary,
            total_invited: data.total_invites ?? prevSummary.total_invited,
            status_breakdown: {
              pending: data.pending_count ?? prevSummary.status_breakdown.pending,
              accepted: data.accepted_count ?? prevSummary.status_breakdown.accepted,
              rejected: data.rejected_count ?? prevSummary.status_breakdown.rejected,
              ignored: data.ignored_count ?? prevSummary.status_breakdown.ignored
            }
          };
          
          // Recalculate response rate
          const totalResponses = newSummary.status_breakdown.accepted + newSummary.status_breakdown.rejected + newSummary.status_breakdown.ignored;
          newSummary.response_rate = newSummary.total_invited > 0 ? (totalResponses / newSummary.total_invited) * 100 : 0;
          
          return newSummary;
        });
        
        // Notify parent component to update guest stats (for top card and dashboard)
        onGuestStatsUpdate?.();
      }
    };

    // Handle event updates (like rescheduling)
    const handleEventUpdate = (data: any) => {
      
      // If the event was rescheduled, refresh the guest list to show reset statuses
      if (data.status === 'rescheduled') {
        fetchGuests();
        onGuestStatsUpdate?.();
      }
    };

    // Handle WhatsApp / image message delivery status updates (from Twilio callback)
    const handleWhatsAppStatusUpdate = (data: { guest_event_id?: string; status?: string; status_type?: string; image_message_status?: string }) => {
      const geId = data.guest_event_id;
      if (!geId) return;
      setGuests(prevGuests =>
        prevGuests.map(g => {
          if (g.id.toString() !== geId) return g;
          if (data.status_type === 'image_message' || data.image_message_status !== undefined) {
            return { ...g, image_message_status: data.image_message_status ?? data.status ?? g.image_message_status };
          }
          return { ...g, whatsapp_status: data.status ?? g.whatsapp_status };
        })
      );
    };

    // Subscribe to real-time updates
    const unsubscribeGuestResponse = webSocketService.on('guest_response', handleGuestResponse);
    const unsubscribeStatistics = webSocketService.on('event_statistics_update', handleStatisticsUpdate);
    const unsubscribeEventUpdate = webSocketService.on('event_updated', handleEventUpdate);
    const unsubscribeWhatsAppStatus = webSocketService.on('whatsapp_status_update', handleWhatsAppStatusUpdate);

    return () => {
      unsubscribeGuestResponse();
      unsubscribeStatistics();
      unsubscribeEventUpdate();
      unsubscribeWhatsAppStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]); // Remove fetchGuests, onGuestStatsUpdate, summary from dependencies to prevent re-subscription


  // Handle status update
  const handleStatusUpdate = async (guestId: number, newStatus: GuestEvent['status']) => {
    try {
      const guest = guests.find(g => g.id === guestId);
      const guestName = guest ? `${guest.guest?.first_name || 'Unknown'} ${guest.guest?.last_name || 'Guest'}` : 'Guest';
      
      await guestAPI.updateGuestStatus(eventId, guestId, { status: newStatus });
      
      const statusMessages = {
        pending: `${guestName} status set to Pending`,
        accepted: `${guestName} confirmed attendance`,
        rejected: `${guestName} declined invitation`,
        ignored: `${guestName} marked as No Response`
      };
      
      setSuccessMessage(statusMessages[newStatus]);
      fetchGuests();
      onGuestStatsUpdate?.();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to update guest status');
    }
  };

  // Handle guest deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState<{ id: number; name: string; willRefund: boolean; refundAmount?: string } | null>(null);
  
  // Handle bulk guest deletion
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteInfo, setBulkDeleteInfo] = useState<{
    guestIds: number[];
    guestsToRefund: Array<{ id: number; name: string; refundAmount?: string }>;
    guestsNoRefund: Array<{ id: number; name: string }>;
    totalRefundAmount: number;
  } | null>(null);

  const handleDeleteGuest = async (guestId: number, guestName: string) => {
    // Refresh guest list first to ensure we have latest data
    await fetchGuests();
    
    // Find the guest from the refreshed list
    let guest: GuestEvent | undefined = guests.find(g => g.id === guestId);
    
    // If not found in list, try fetching directly
    if (!guest) {
      try {
        const latestGuest = await guestAPI.getGuest(eventId, guestId);
        guest = latestGuest;
      } catch (error) {
        // Fallback to cached guest data if fetch fails
        guest = guests.find(g => g.id === guestId);
      }
    }
    
    const willRefund = !!(guest?.base_fee_charged && (guest?.whatsapp_message_count || 0) === 0);
    const refundAmount = guest?.base_fee_amount;

    setGuestToDelete({
      id: guestId,
      name: guestName,
      willRefund,
      refundAmount
    });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteGuest = async () => {
    if (!guestToDelete) return;

    try {
      await guestAPI.deleteGuest(eventId, guestToDelete.id);
      setSuccessMessage(`${guestToDelete.name} has been removed from the event${guestToDelete.willRefund ? ' (guest base fee refunded)' : ''}`);
      fetchGuests();
      onGuestStatsUpdate?.();
      setShowDeleteConfirm(false);
      setGuestToDelete(null);
      // Note: selectedGuests contains User IDs, but guestId is GuestEvent ID
      // So we don't need to remove it from selectedGuests
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to remove guest');
      setShowDeleteConfirm(false);
      setGuestToDelete(null);
    }
  };

  const confirmBulkDeleteGuests = async () => {
    if (!bulkDeleteInfo) return;

    try {
      await guestAPI.bulkAction(eventId, {
        action: 'delete',
        guest_ids: bulkDeleteInfo.guestIds
      });
      
      const refundMessage = bulkDeleteInfo.guestsToRefund.length > 0
        ? ` (${bulkDeleteInfo.guestsToRefund.length} guest${bulkDeleteInfo.guestsToRefund.length > 1 ? 's' : ''} refunded)`
        : '';
      
      setSuccessMessage(`${bulkDeleteInfo.guestIds.length} guest${bulkDeleteInfo.guestIds.length > 1 ? 's' : ''} removed successfully${refundMessage}`);
      setSelectedGuests(new Set());
      fetchGuests();
      onGuestStatsUpdate?.();
      setShowBulkDeleteConfirm(false);
      setBulkDeleteInfo(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to remove guests');
      setShowBulkDeleteConfirm(false);
      setBulkDeleteInfo(null);
    }
  };

  // Handle guest selection
  const handleSelectGuest = (guestId: string, selected: boolean) => {
    setSelectedGuests(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(guestId);
      } else {
        newSet.delete(guestId);
      }
      return newSet;
    });
  };

  const buildGuestFilterParams = useCallback((): Omit<GuestListParams, 'page' | 'page_size'> => {
    const trimmedSearch = debouncedSearchTerm.trim();
    const params: Omit<GuestListParams, 'page' | 'page_size'> = {};
    if (statusFilter.length > 0) params.status = statusFilter as GuestListParams['status'];
    if (trimmedSearch) params.search = trimmedSearch;
    if (importFilter === 'imported') params.imported_only = true;
    if (importFilter === 'manual') params.manual_only = true;
    if (sortBy) params.ordering = sortBy;
    return params;
  }, [debouncedSearchTerm, statusFilter, importFilter, sortBy]);

  const resolveSelectedGuestEvents = useCallback(async (): Promise<GuestEvent[]> => {
    return guestAPI.resolveSelectedGuestEvents(
      eventId,
      Array.from(selectedGuests)
    );
  }, [eventId, selectedGuests]);

  const resolveSelectedGuestUserIds = useCallback(async (): Promise<string[]> => {
    const guestEvents = await resolveSelectedGuestEvents();
    return guestEvents.map((guest) => guest.guest.id);
  }, [resolveSelectedGuestEvents]);

  const getSelectedGuestNames = useCallback(async (): Promise<Array<{ id: string; name: string }>> => {
    const ids = Array.from(selectedGuests);
    const namesById = new Map<string, string>();

    guests.forEach((guest) => {
      const guestEventId = guest.id.toString();
      if (selectedGuests.has(guestEventId)) {
        namesById.set(guestEventId, formatGuestEventDisplayName(guest));
      }
    });

    const missingIds = ids.filter((id) => !namesById.has(id));
    if (missingIds.length > 0) {
      const resolved = await resolveSelectedGuestEvents();
      resolved.forEach((guest) => {
        namesById.set(guest.id.toString(), formatGuestEventDisplayName(guest));
      });
    }

    return ids.map((id) => ({
      id,
      name: namesById.get(id) || 'Unknown Guest',
    }));
  }, [selectedGuests, guests, resolveSelectedGuestEvents]);

  useEffect(() => {
    if (!showWhatsAppConfirm || selectedGuests.size === 0) {
      setResolvedSelectedGuests([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setResolvingSelection(true);
      try {
        const resolved = await guestAPI.resolveSelectedGuestEvents(
          eventId,
          Array.from(selectedGuests)
        );
        if (!cancelled) {
          setResolvedSelectedGuests(resolved);
        }
      } catch (err: any) {
        if (!cancelled) {
          logError('Error resolving selected guests', err);
          setErrorMessage(err.response?.data?.message || 'Failed to load selected guests');
        }
      } finally {
        if (!cancelled) {
          setResolvingSelection(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showWhatsAppConfirm, selectedGuests, eventId]);

  useEffect(() => {
    if (selectedGuests.size === 0) {
      setHasWhatsAppSentInSelection(false);
      return;
    }

    const selectedOnPage = guests.filter((guest) => selectedGuests.has(guest.id.toString()));
    const allSelectionOnCurrentPage = selectedOnPage.length === selectedGuests.size;

    if (allSelectionOnCurrentPage) {
      setHasWhatsAppSentInSelection(selectedOnPage.some((guest) => guest.whatsapp_status === 'sent'));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resolved = await guestAPI.resolveSelectedGuestEvents(
          eventId,
          Array.from(selectedGuests)
        );
        if (!cancelled) {
          setHasWhatsAppSentInSelection(resolved.some((guest) => guest.whatsapp_status === 'sent'));
        }
      } catch (err: any) {
        if (!cancelled) {
          logError('Error checking WhatsApp status for selection', err);
          setHasWhatsAppSentInSelection(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedGuests, guests, eventId]);

  const handleSelectAll = async (selected: boolean) => {
    if (!selected) {
      setSelectedGuests(new Set());
      return;
    }

    setSelectingAll(true);
    try {
      const allIds = await guestAPI.getAllGuestEventIds(eventId, buildGuestFilterParams());
      setSelectedGuests(new Set(allIds));
    } catch (err: any) {
      logError('Error selecting all guests', err);
      setErrorMessage(err.response?.data?.message || 'Failed to select all guests');
    } finally {
      setSelectingAll(false);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action: string, newStatus?: string) => {
    if (selectedGuests.size === 0) return;

    try {
      const guestIds = Array.from(selectedGuests).map(id => parseInt(id));
      
      switch (action) {
        case 'delete': {
          const selectedGuestEvents = await resolveSelectedGuestEvents();
          const guestsToRefund: Array<{ id: number; name: string; refundAmount?: string }> = [];
          const guestsNoRefund: Array<{ id: number; name: string }> = [];
          let totalRefundAmount = 0;
          
          selectedGuestEvents.forEach(guest => {
            const willRefund = !!(guest.base_fee_charged && (guest.whatsapp_message_count || 0) === 0);
            const guestName = `${guest.guest?.first_name || 'Unknown'} ${guest.guest?.last_name || 'Guest'}`.trim();
            
            if (willRefund) {
              const refundAmount = parseFloat(guest.base_fee_amount || '0');
              guestsToRefund.push({
                id: guest.id,
                name: guestName,
                refundAmount: guest.base_fee_amount
              });
              totalRefundAmount += refundAmount;
            } else {
              guestsNoRefund.push({
                id: guest.id,
                name: guestName
              });
            }
          });
          
          setBulkDeleteInfo({
            guestIds,
            guestsToRefund,
            guestsNoRefund,
            totalRefundAmount
          });
          setShowBulkDeleteConfirm(true);
          break;
        }

        case 'update_status':
          if (newStatus) {
            await guestAPI.bulkAction(eventId, {
              action: 'update_status',
              guest_ids: guestIds,
              new_status: newStatus
            });
            const statusMessages = {
              pending: 'Pending',
              accepted: 'Confirmed',
              rejected: 'Declined',
              ignored: 'No Response'
            };
            setSuccessMessage(`${selectedGuests.size} guests marked as ${statusMessages[newStatus as keyof typeof statusMessages]}`);
            setSelectedGuests(new Set());
            fetchGuests();
            onGuestStatsUpdate?.();
          }
          break;
        
        case 'export_selected':
          await guestAPI.bulkAction(eventId, {
            action: 'export',
            guest_ids: guestIds,
            export_format: 'csv'
          });
          // Handle export download
          setSuccessMessage(`${selectedGuests.size} guests exported successfully`);
          break;
          
        default:
          break;
      }
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Bulk action failed');
    }
  };

  // Handle showing WhatsApp confirmation
  const handleShowWhatsAppConfirmation = () => {
    if (selectedGuests.size === 0) {
      setErrorMessage('Please select guests to send invitations to');
      return;
    }
    setPendingSendType('whatsapp');
    setShowSendByNameModal(true);
  };
  
  // Handle send by name modal confirm - go directly to action confirmation
  const handleSendByNameConfirm = async (sendByNameValue: boolean) => {
    setSendByName(sendByNameValue);
    setShowSendByNameModal(false);
    setPendingSendByNameValue(sendByNameValue);
    
    if (pendingSendType === 'whatsapp') {
      // Go directly to WhatsApp confirmation
      setPendingAction(() => () => handleSendWhatsApp(sendByNameValue));
      setShowWhatsAppConfirm(true);
    } else if (pendingSendType === 'reminder') {
      // Go directly to reminder confirmation
      setPendingAction(() => () => handleSendReminders(sendByNameValue));
      setShowReminderConfirm(true);
    } else if (pendingSendType === 'followup') {
      // Open the confirmation table and resolve who would actually be messaged
      setShowFollowUpConfirm(true);
      void loadFollowUpRecipients();
    }

    setPendingSendType(null);
  };

  // Actual WhatsApp send function
  const handleSendWhatsApp = async (sendByNameValue: boolean = true) => {
    if (selectedGuests.size === 0) {
      return;
    }

    setShowWhatsAppConfirm(false);

    try {
      setLoading(true);
      setError(null);

      // Reuse guests resolved for the confirmation modal when available
      const guestIds =
        resolvedSelectedGuests.length >= selectedGuests.size
          ? resolvedSelectedGuests.map((guest) => guest.guest.id)
          : await resolveSelectedGuestUserIds();
      if (guestIds.length === 0) {
        setErrorMessage('Could not resolve selected guests. Please try again.');
        return;
      }
      
      const result = await guestAPI.sendWhatsAppInvitations(eventId, guestIds, sendByNameValue);

      // Check if all failed, partial success, or all successful
      if (result.all_failed || !result.success) {
        setErrorMessage(result.message || 'Failed to send WhatsApp invitations');
      } else if (result.partial) {
        // Show as warning/info message for partial success
        setSuccessMessage(`⚠️ ${result.message}`);
      } else {
        setSuccessMessage(result.message);
      }
      
      setSelectedGuests(new Set());
      
      // Update local state instead of refreshing the entire list
      if (result.data && result.data.results) {
        setGuests(prevGuests => 
          prevGuests.map(guest => {
            const guestEventId = guest.id;
            const resultData = result.data.results.find(r => 
              r.guest_event_id === guestEventId.toString() || 
              r.guest_id === guest.guest.id
            );
            
            if (resultData && resultData.result.success) {
              return {
                ...guest,
                whatsapp_sent_at: new Date().toISOString(),
                whatsapp_message_sid: resultData.result.message_sid,
                whatsapp_status: 'sent',
                whatsapp_message_count: (guest.whatsapp_message_count || 0) + 1 // Increment message count
              };
            } else if (resultData && !resultData.result.success) {
              // Mark as failed if the send failed
              return {
                ...guest,
                whatsapp_status: 'failed'
              };
            }
            return guest;
          })
        );
      }
      
      onGuestStatsUpdate?.();
    } catch (err: any) {
      logError('Failed to send WhatsApp invitations', err);
      setErrorMessage(
        err.response?.data?.message || 'Failed to send WhatsApp invitations');
    } finally {
      setLoading(false);
    }
  };

  // Handle showing reminder confirmation
  const handleShowReminderConfirmation = (forceCount?: number) => {
    const count = forceCount !== undefined ? forceCount : selectedGuests.size;
    if (count === 0) {
      setErrorMessage('Please select guests to send reminders to');
      return;
    }
    setPendingSendType('reminder');
    setShowSendByNameModal(true);
  };

  // Actual reminder send function
  const handleSendReminders = async (sendByNameValue: boolean = true) => {
    
    if (selectedGuests.size === 0) {
      return;
    }

    setShowReminderConfirm(false);

    try {
      setLoading(true);
      setError(null);

      // Convert selected GuestEvent IDs to guest user IDs (UUIDs) across all pages
      const guestIds = await resolveSelectedGuestUserIds();
      if (guestIds.length === 0) {
        setErrorMessage('Could not resolve selected guests. Please try again.');
        return;
      }
      
      const result = await guestAPI.sendReminders(eventId, guestIds, sendByNameValue);
      
      // Check if all failed, partial success, or all successful
      if (result.all_failed || !result.success) {
        setErrorMessage(result.message || 'Failed to send reminders');
      } else if (result.partial) {
        setSuccessMessage(`⚠️ ${result.message}`);
      } else {
        setSuccessMessage(result.message);
      }
      
      setSelectedGuests(new Set());
      await fetchGuests();
      onGuestStatsUpdate?.();
    } catch (err: any) {
      logError('Failed to send reminders', err);
      setErrorMessage(
        err.response?.data?.message || 'Failed to send reminders');
    } finally {
      setLoading(false);
    }
  };

  // Handle showing follow-up confirmation
  const handleShowFollowUpConfirmation = () => {
    if (selectedGuests.size === 0) {
      setErrorMessage('Please select guests to follow up with');
      return;
    }
    setPendingSendType('followup');
    setShowSendByNameModal(true);
  };

  // Ask the backend who a follow-up would reach, so the confirmation table is an exact
  // preview of the send rather than a client-side guess.
  const loadFollowUpRecipients = async () => {
    setLoadingFollowUpRecipients(true);
    setFollowUpLoadError(null);
    setFollowUpRecipients([]);
    setFollowUpExcludedIds(new Set());

    try {
      const guestIds = await resolveSelectedGuestUserIds();
      if (guestIds.length === 0) {
        setFollowUpLoadError('Could not resolve selected guests. Please try again.');
        return;
      }
      setFollowUpRecipients(await guestAPI.previewFollowUps(eventId, guestIds));
    } catch (err: any) {
      logError('Failed to load follow-up recipients', err);
      setFollowUpLoadError(
        err.response?.data?.message || 'Failed to work out who needs a follow-up.'
      );
    } finally {
      setLoadingFollowUpRecipients(false);
    }
  };

  const handleToggleFollowUpRecipient = (guestEventId: string) => {
    setFollowUpExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(guestEventId)) {
        next.delete(guestEventId);
      } else {
        next.add(guestEventId);
      }
      return next;
    });
  };

  // Actual follow-up send function. The backend decides who actually gets a message -
  // guests and companions that already responded are skipped there, plus any row the
  // host unticked in the confirmation table.
  const handleSendFollowUps = async (
    sendByNameValue: boolean = true,
    excludedGuestEventIds: string[] = []
  ) => {
    if (selectedGuests.size === 0) {
      return;
    }

    setShowFollowUpConfirm(false);

    try {
      setLoading(true);
      setError(null);

      // Convert selected GuestEvent IDs to guest user IDs (UUIDs) across all pages
      const guestIds = await resolveSelectedGuestUserIds();
      if (guestIds.length === 0) {
        setErrorMessage('Could not resolve selected guests. Please try again.');
        return;
      }

      const result = await guestAPI.sendFollowUps(
        eventId,
        guestIds,
        sendByNameValue,
        excludedGuestEventIds
      );

      // Check if all failed, partial success, or all successful
      if (result.all_failed || !result.success) {
        setErrorMessage(result.message || 'Failed to send follow-ups');
      } else if (result.partial) {
        setSuccessMessage(`⚠️ ${result.message}`);
      } else {
        setSuccessMessage(result.message);
      }

      setSelectedGuests(new Set());
      await fetchGuests();
      onGuestStatsUpdate?.();
    } catch (err: any) {
      logError('Failed to send follow-ups', err);
      setErrorMessage(
        err.response?.data?.message || 'Failed to send follow-ups');
    } finally {
      setLoading(false);
    }
  };

  // Handle showing image message cost confirmation
  const handleShowImageMessageCostConfirm = async (
    imageUrl: string,
    imageFilePath: string,
    imageMessageBody?: string
  ) => {
    setPendingImageUrl(imageUrl);
    setPendingImageFilePath(imageFilePath);
    setPendingImageMessageBody((imageMessageBody || '').trim());
    // Fetch billing data BEFORE showing the modal to prevent the "pop" effect
    try {
      setBillingLoading(true);
      const [pricingData, balanceData] = await Promise.all([
        billingService.getCurrentPricing().catch(() => null),
        billingService.getMyBalance().catch(() => null)
      ]);
      setPricing(pricingData);
      setBalance(balanceData);
    } catch (error) {
      logError('Failed to fetch billing data', error);
    } finally {
      setBillingLoading(false);
      // Show confirmation modal (ImageMessageModal is already closed by this point)
      setShowImageMessageConfirm(true);
    }
  };

  // Handle image message sending
  const handleSendImageMessage = async (
    imageUrl?: string,
    imageFilePath?: string,
    imageMessageBodyArg?: string
  ) => {
    const urlToSend = imageUrl ?? pendingImageUrl;
    const filePathToSend = imageFilePath ?? pendingImageFilePath;
    if (!urlToSend) return;

    try {
      setLoading(true);
      setError(null);

      const guestIds = await resolveSelectedGuestUserIds();
      if (guestIds.length === 0) {
        setErrorMessage('Could not resolve selected guests. Please try again.');
        return;
      }

      const caption = (imageMessageBodyArg ?? pendingImageMessageBody).trim();

      const body: { guest_ids: string[]; image_url: string; image_file_path?: string; image_message_body?: string } = {
        guest_ids: guestIds,
        image_url: urlToSend
      };
      if (filePathToSend) body.image_file_path = filePathToSend;
      if (caption) body.image_message_body = caption;

      const response = await apiClient.post(`/events/${eventId}/send-image-message/`, body);
      const data = response.data;

      const successMsg = `Image message sent successfully to ${data.results.successful} guest${data.results.successful > 1 ? 's' : ''}`;

      setSuccessMessage(successMsg);
      setShowImageMessageConfirm(false);
      setPendingImageUrl(null);
      setPendingImageFilePath(null);
      setPendingImageMessageBody('');
      setSelectedGuests(new Set());
      fetchGuests();
      onGuestStatsUpdate?.();
    } catch (error: any) {
      logError('Error sending image message', error);
      setErrorMessage(error.response?.data?.message || 'Failed to send image message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle image message button click
  const handleShowImageMessageModal = () => {
    if (selectedGuests.size === 0) {
      setErrorMessage('Please select guests to send image message to');
      return;
    }
    setShowImageMessageModal(true);
  };

  // Reset billing data when modals close to ensure fresh data on next open
  useEffect(() => {
    if (!showSaveTheDateConfirm && !showImageMessageConfirm) {
      // Reset billing data when modals close to ensure fresh data on next open
      setPricing(null);
      setBalance(null);
    }
  }, [showSaveTheDateConfirm, showImageMessageConfirm]);

  // Calculate costs for save the date
  const calculateSaveTheDateCosts = useCallback((): CostBreakdown[] => {
    if (!pricing || selectedGuests.size === 0) return [];
    return [{
      label: `Save the Date Fee (${selectedGuests.size} guest${selectedGuests.size !== 1 ? 's' : ''})`,
      amount: selectedGuests.size * parseFloat(pricing.save_the_date_fee),
      quantity: selectedGuests.size,
      unitPrice: parseFloat(pricing.save_the_date_fee)
    }];
  }, [pricing, selectedGuests.size]);

  // Calculate costs for image message
  const calculateImageMessageCosts = useCallback((): CostBreakdown[] => {
    if (!pricing || selectedGuests.size === 0) return [];
    return [{
      label: `Image Message Fee (${selectedGuests.size} guest${selectedGuests.size !== 1 ? 's' : ''})`,
      amount: selectedGuests.size * parseFloat(pricing.image_message_fee),
      quantity: selectedGuests.size,
      unitPrice: parseFloat(pricing.image_message_fee)
    }];
  }, [pricing, selectedGuests.size]);

  // Handle showing save the date confirmation - go directly to action confirmation
  const handleShowSaveTheDateConfirmation = async () => {
    if (selectedGuests.size === 0) {
      setErrorMessage('Please select guests to send save the date messages to');
      return;
    }
    
    // Fetch billing data BEFORE showing the modal to prevent the "pop" effect
    try {
      setBillingLoading(true);
      const [pricingData, balanceData] = await Promise.all([
        billingService.getCurrentPricing().catch(() => null),
        billingService.getMyBalance().catch(() => null)
      ]);
      setPricing(pricingData);
      setBalance(balanceData);
    } catch (error) {
      logError('Failed to fetch billing data', error);
    } finally {
      setBillingLoading(false);
      // Go directly to save the date confirmation
      setPendingAction(() => () => handleSendSaveTheDate());
      setShowSaveTheDateConfirm(true);
    }
  };

  // Actual save the date send function
  const handleSendSaveTheDate = async () => {
    if (selectedGuests.size === 0) {
      return;
    }
    setShowSaveTheDateConfirm(false);

    try {
      setLoading(true);
      setError(null);

      // Convert selected GuestEvent IDs to guest user IDs (UUIDs) across all pages
      const guestIds = await resolveSelectedGuestUserIds();
      if (guestIds.length === 0) {
        setErrorMessage('Could not resolve selected guests. Please try again.');
        return;
      }
      
      const result = await guestAPI.sendSaveTheDate(eventId, guestIds);
      
      // Check if all failed, partial success, or all successful
      if (result.all_failed || !result.success) {
        setErrorMessage(result.message || 'Failed to send save the date messages');
      } else if (result.partial) {
        setSuccessMessage(`⚠️ ${result.message}`);
      } else {
        setSuccessMessage(result.message);
      }
      
      setSelectedGuests(new Set());
      await fetchGuests();
      onGuestStatsUpdate?.();
    } catch (err: any) {
      logError('Failed to send save the date messages', err);
      setErrorMessage(
        err.response?.data?.message || 'Failed to send save the date messages');
    } finally {
      setLoading(false);
    }
  };

  // Quick actions from stats dashboard
  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'add':
        setShowAddForm(true);
        break;
      case 'import':
        setShowImportModal(true);
        break;
      case 'export':
        handleExportGuests();
        break;
      case 'remind':
        // Handle send reminders to pending guests
        const pendingGuests = guests.filter(g => g.status === 'pending');
        if (pendingGuests.length > 0) {
          const pendingGuestIds = pendingGuests.map(g => g.id.toString());
          setSelectedGuests(new Set(pendingGuestIds));
          handleShowReminderConfirmation(pendingGuests.length);
        } else {
          setErrorMessage('No pending guests to send reminders to');
        }
        break;
    }
  };

  const handleExportGuests = async () => {
    try {
      setExporting(true);
      setError(null);

      // Call the backend API to get export data
      const result = await guestAPI.bulkAction(eventId, {
        action: 'export',
        guest_ids: guests.map(g => g.id),
        export_format: 'excel'
      });

      if (result.success && result.export_data) {
        // Format the data for Excel export
        const formattedData = ExcelExportUtils.formatGuestDataForExport(result.export_data);
        
        // Generate filename with event info
        const filename = ExcelExportUtils.generateFilename(`event_${eventId}_guests`);
        
        // Export to Excel with enhanced formatting
        ExcelExportUtils.exportToExcel(formattedData, filename, 'Guests', {
          autoWidth: true,
          freezeHeader: true,
          addFilters: true
        });
        
        setSuccessMessage(`Successfully exported ${result.affected_count} guests to Excel`);
      } else {
        setErrorMessage('Export failed: No data received from server');
      }
    } catch (err: any) {
      logError('Export error', err);
      setErrorMessage(err.response?.data?.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleGuestAdded = () => {
    fetchGuests();
    setShowAddForm(false);
    setSuccessMessage('Guest added successfully');
    onGuestStatsUpdate?.();
  };

  const handleImportComplete = () => {
    fetchGuests();
    setShowImportModal(false);
    setSuccessMessage('Guests imported successfully');
    onGuestStatsUpdate?.();
  };

  const handleEditCompanions = (guestId: number, guest: GuestEvent) => {
    setEditingGuest(guest);
    setShowEditCompanions(true);
  };

  const handleCompanionUpdate = (closeModal: boolean = true) => {
    fetchGuests();
    if (closeModal) {
      setSuccessMessage('Companion information updated successfully');
      setShowEditCompanions(false);
      setEditingGuest(null);
    }
    onGuestStatsUpdate?.();
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {successMessage && (
        <Toast
          message={successMessage}
          type="success"
          onClose={() => setSuccessMessage(null)}
        />
      )}
      {errorMessage && (
        <Toast
          message={errorMessage}
          type="error"
          onClose={() => setErrorMessage(null)}
        />
      )}

      {/* Capacity Warning - Only show when limit is reached or exceeded */}
      {event?.guest_limit && summary && summary.total_attendees > event.guest_limit && (
        <div className={`mb-4 p-4 rounded-lg border ${
          theme === 'dark'
            ? 'bg-yellow-900/20 border-yellow-700/50'
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-start">
            <div className={`flex-shrink-0 ${
              theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
            }`}>
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium ${
                theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
              }`}>
                Capacity Exceeded
              </h3>
              <div className={`mt-2 text-sm ${
                theme === 'dark' ? 'text-yellow-200' : 'text-yellow-700'
              }`}>
                <p>
                  You have {summary.total_attendees} accepted attendees, which exceeds the capacity limit of {event.guest_limit}.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <GuestActionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        importFilter={importFilter}
        onImportFilterChange={setImportFilter}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        sortBy={sortBy}
        onSortChange={setSortBy}
        selectedCount={selectedGuests.size}
        totalCount={totalCount}
        hasWhatsAppSentInSelection={hasWhatsAppSentInSelection}
        onAddGuest={() => setShowAddForm(true)}
        onImportGuests={() => setShowImportModal(true)}
        onExportGuests={handleExportGuests}
        onBulkAction={handleBulkAction}
        onSendWhatsApp={handleShowWhatsAppConfirmation}
        onSendReminders={handleShowReminderConfirmation}
        onSendFollowUp={handleShowFollowUpConfirmation}
        onSendSaveTheDate={handleShowSaveTheDateConfirmation}
        onSendImageMessage={handleShowImageMessageModal}
        onClearSelection={() => setSelectedGuests(new Set())}
        onGetSelectedGuestNames={getSelectedGuestNames}
        onRefresh={fetchGuests}
        loading={loading}
        exporting={exporting}
        theme={theme}
        eventHasEnded={eventHasEnded || !canEdit}
        guestLimit={event?.guest_limit}
        currentGuestCount={summary?.total_invited ?? guests.length}
      />

      {/* Guest Display */}
      <GuestDisplay
        guests={guests}
        eventId={eventId}
        viewMode={viewMode}
        loading={loading}
        selectedGuests={selectedGuests}
        selectingAll={selectingAll}
        onSelectGuest={handleSelectGuest}
        onSelectAll={handleSelectAll}
        onStatusUpdate={handleStatusUpdate}
        onDeleteGuest={handleDeleteGuest}
        onEditCompanions={handleEditCompanions}
        onSendReminder={(guestId) => {
          setSelectedGuests(new Set([guestId]));
          handleShowReminderConfirmation(1);
        }}
        onRefresh={fetchGuests}
        onError={setErrorMessage}
        theme={theme}
        eventHasEnded={eventHasEnded || !canEdit}
        pagination={{
          currentPage,
          totalPages,
          pageSize,
          totalCount,
          onPageChange: setCurrentPage,
          onPageSizeChange: (newSize) => {
            setPageSize(newSize);
            setCurrentPage(1);
          }
        }}
      />

      {/* Error State */}
      {error && (
        <div className={`border rounded-xl p-6 text-center ${
          theme === 'dark' 
            ? 'bg-red-900/20 border-red-700/50' 
            : 'bg-red-50 border-red-200'
        }`}>
          <h3 className={`text-lg font-medium mb-2 ${
            theme === 'dark' ? 'text-red-300' : 'text-red-900'
          }`}>Unable to load guests</h3>
          <p className={`mb-4 ${
            theme === 'dark' ? 'text-red-400' : 'text-red-700'
          }`}>{error}</p>
          <button
            onClick={fetchGuests}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Modals */}
      <AddGuestFormEnhanced
        eventId={eventId}
        isOpen={showAddForm}
        onClose={() => setShowAddForm(false)}
        onSuccess={handleGuestAdded}
      />

      <GuestImportModal
        eventId={eventId}
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={handleImportComplete}
      />

      <EditCompanionModal
        isOpen={showEditCompanions}
        onClose={() => setShowEditCompanions(false)}
        eventHasEnded={eventHasEnded || !canEdit}
        guest={editingGuest}
        eventId={eventId}
        onUpdate={handleCompanionUpdate}
      />

      <ImageMessageModal
        isOpen={showImageMessageModal}
        onClose={() => setShowImageMessageModal(false)}
        onSend={handleSendImageMessage}
        onShowCostConfirm={handleShowImageMessageCostConfirm}
        selectedCount={selectedGuests.size}
        theme={theme}
        loading={loading}
      />

      {/* Image Message Cost Confirmation Modal */}
      <ConfirmationModal
        isOpen={showImageMessageConfirm}
        onClose={() => {
          setShowImageMessageConfirm(false);
          setPendingImageUrl(null);
          setPendingImageFilePath(null);
          setPendingImageMessageBody('');
        }}
        onConfirm={() => handleSendImageMessage()}
        title="Send Image Message"
        message=""
        confirmText="Send"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={loading}
      >
        {!billingLoading && pricing ? (
          <BillingActionPreview
            costs={calculateImageMessageCosts()}
            showBalance={true}
            showBalanceAfter={true}
            currentBalance={balance}
            pricing={pricing}
            showWarning={true}
            compact={false}
            title="Cost Preview"
            infoText="Fee is charged per message sent."
          />
        ) : (
          <div className={`p-4 rounded-lg border ${
            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Loading cost information...
            </p>
          </div>
        )}
      </ConfirmationModal>

      {/* Send by Name Modal */}
      <SendByNameModal
        isOpen={showSendByNameModal}
        onClose={() => {
          setShowSendByNameModal(false);
          setPendingSendType(null);
        }}
        onConfirm={handleSendByNameConfirm}
        title={
          pendingSendType === 'whatsapp'
            ? 'Send WhatsApp Invitations'
            : pendingSendType === 'followup'
              ? 'Send Follow-Ups'
              : 'Send Reminders'
        }
        message={`How would you like to address ${selectedGuests.size} guest${selectedGuests.size > 1 ? 's' : ''}? Arabic vs English uses each guest’s “Invite in English” setting from when they were added (edit guest to change).`}
        confirmText="Continue"
        cancelText="Cancel"
        isLoading={loading}
      />

      {/* WhatsApp Confirmation Modal with Companion Info */}
      <BulkInviteConfirmationModal
        isOpen={showWhatsAppConfirm}
        onClose={() => setShowWhatsAppConfirm(false)}
        onConfirm={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        totalSelectedCount={selectedGuests.size}
        selectedGuestEvents={resolvedSelectedGuests}
        confirmText="Send Invitations"
        cancelText="Cancel"
        isLoading={loading}
        isResolvingSelection={resolvingSelection}
      />

      {/* Reminder Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReminderConfirm}
        onClose={() => setShowReminderConfirm(false)}
        onConfirm={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        title="Send Reminders"
        message={`Are you sure you want to send reminder messages to ${selectedGuests.size} guest${selectedGuests.size > 1 ? 's' : ''}?`}
        confirmText="Send"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={loading}
      />

      {/* Follow-Up Confirmation Modal with per-recipient send flags */}
      <FollowUpConfirmationModal
        isOpen={showFollowUpConfirm}
        onClose={() => setShowFollowUpConfirm(false)}
        onConfirm={(excludedGuestEventIds) =>
          handleSendFollowUps(pendingSendByNameValue, excludedGuestEventIds)
        }
        recipients={followUpRecipients}
        excludedIds={followUpExcludedIds}
        onToggleRecipient={handleToggleFollowUpRecipient}
        isLoadingRecipients={loadingFollowUpRecipients}
        loadError={followUpLoadError}
        isLoading={loading}
      />

      {/* Delete Guest Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setGuestToDelete(null);
        }}
        onConfirm={confirmDeleteGuest}
        title="Remove Guest"
        message={guestToDelete 
          ? `Are you sure you want to remove ${guestToDelete.name} from this event?`
          : ''
        }
        confirmText="Confirm"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={loading}
      >
        {guestToDelete && (
          <div className={`mt-3 p-3 rounded-lg ${
            theme === 'dark' 
              ? guestToDelete.willRefund ? 'bg-green-900/30 border border-green-700/50' : 'bg-yellow-900/30 border border-yellow-700/50'
              : guestToDelete.willRefund ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            {guestToDelete.willRefund ? (
              <div className="flex items-start gap-2">
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-green-400' : 'text-green-700'
                }`}>
                  <p className="font-medium">✓ Refund will be issued</p>
                  <p className="mt-1 text-xs">
                    The guest base fee ({guestToDelete.refundAmount || 'N/A'} JOD) will be refunded to your balance since no message was sent to this guest.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-yellow-400' : 'text-yellow-700'
                }`}>
                  <p className="font-medium">⚠ No refund will be issued</p>
                  <p className="mt-1 text-xs">
                    A message was already sent to this guest, so the guest base fee cannot be refunded.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </ConfirmationModal>

      {/* Save the Date Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSaveTheDateConfirm}
        onClose={() => setShowSaveTheDateConfirm(false)}
        onConfirm={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        title="Save the Date"
        message=""
        confirmText="Send"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={loading}
      >
        {!billingLoading && pricing ? (
          <BillingActionPreview
            costs={calculateSaveTheDateCosts()}
            showBalance={true}
            showBalanceAfter={true}
            currentBalance={balance}
            pricing={pricing}
            showWarning={true}
            compact={false}
            title="Cost Preview"
            infoText="Fee is charged per message sent."
          />
        ) : (
          <div className={`p-4 rounded-lg border ${
            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Loading cost information...
            </p>
          </div>
        )}
      </ConfirmationModal>

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => {
          setShowBulkDeleteConfirm(false);
          setBulkDeleteInfo(null);
        }}
        onConfirm={confirmBulkDeleteGuests}
        title="Remove Selected Guests"
        message={bulkDeleteInfo 
          ? `Are you sure you want to remove ${bulkDeleteInfo.guestIds.length} guest${bulkDeleteInfo.guestIds.length > 1 ? 's' : ''} from this event?`
          : ''
        }
        confirmText="Confirm"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={loading}
      >
        {bulkDeleteInfo && (
          <div className="mt-3 space-y-3">
            {bulkDeleteInfo.guestsToRefund.length > 0 && (
              <div className={`p-3 rounded-lg ${
                theme === 'dark' 
                  ? 'bg-green-900/30 border border-green-700/50' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-start gap-2">
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-green-400' : 'text-green-700'
                  }`}>
                    <p className="font-medium">✓ Refunds will be issued ({bulkDeleteInfo.guestsToRefund.length} guest{bulkDeleteInfo.guestsToRefund.length > 1 ? 's' : ''})</p>
                    <p className="mt-1 text-xs">
                      The guest base fee will be refunded for these guests since no message was sent to them.
                      {bulkDeleteInfo.totalRefundAmount > 0 && (
                        <> Total refund: {bulkDeleteInfo.totalRefundAmount.toFixed(2)} JOD</>
                      )}
                    </p>
                    {bulkDeleteInfo.guestsToRefund.length <= 5 && (
                      <ul className="mt-2 text-xs list-disc list-inside">
                        {bulkDeleteInfo.guestsToRefund.map(guest => (
                          <li key={guest.id}>
                            {guest.name} ({guest.refundAmount || 'N/A'} JOD)
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {bulkDeleteInfo.guestsNoRefund.length > 0 && (
              <div className={`p-3 rounded-lg ${
                theme === 'dark' 
                  ? 'bg-yellow-900/30 border border-yellow-700/50' 
                  : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-start gap-2">
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-yellow-400' : 'text-yellow-700'
                  }`}>
                    <p className="font-medium">⚠ No refunds will be issued ({bulkDeleteInfo.guestsNoRefund.length} guest{bulkDeleteInfo.guestsNoRefund.length > 1 ? 's' : ''})</p>
                    <p className="mt-1 text-xs">
                      A message was already sent to these guests, so the guest base fee cannot be refunded.
                    </p>
                    {bulkDeleteInfo.guestsNoRefund.length <= 5 && (
                      <ul className="mt-2 text-xs list-disc list-inside">
                        {bulkDeleteInfo.guestsNoRefund.map(guest => (
                          <li key={guest.id}>{guest.name}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </ConfirmationModal>
    </div>
  );
};